import {useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import {Box, Typography} from '@mui/material';

import {
  departmentHealthSectionCardSx,
  departmentHealthSectionSubtitleSx,
  departmentHealthSectionTitleSx,
} from './departmentHealthSectionStyles';
import {DepartmentHealthSkillCoverageMapSkeleton} from './DepartmentHealthSkillCoverageMapSkeleton';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {scorePill} from '../../../../../layouts/theme/designSystem';
import {getLocalisedPropertyValue} from '../../../../../utils/getLocalisedPropertyValue';
import {
  DEPARTMENT_HEALTH_SKILL_COVERAGE_DISPLAY_LIMIT,
  type DepartmentHealthSkillCoverage,
  type DepartmentHealthSkillCoverageItem,
} from '../../types/DepartmentHealthTypes';

export interface Props {
  skillCoverage?: DepartmentHealthSkillCoverage;
  loading?: boolean;
  embedded?: boolean;
}

type CoverageColumnKey = 'critical' | 'partial' | 'full';

type CoverageColumnConfig = {
  key: CoverageColumnKey;
  labelKey: string;
  color: string;
  textColor: string;
};

const coverageColumns: CoverageColumnConfig[] = [
  {key: 'critical', labelKey: 'columns.critical', color: scorePill.low.strokeColor, textColor: scorePill.low.color},
  {key: 'partial', labelKey: 'columns.partial', color: scorePill.medium.strokeColor, textColor: scorePill.medium.color},
  {key: 'full', labelKey: 'columns.full', color: scorePill.high.strokeColor, textColor: scorePill.high.color},
];

const limitItems = (items: DepartmentHealthSkillCoverageItem[]) =>
  items.slice(0, DEPARTMENT_HEALTH_SKILL_COVERAGE_DISPLAY_LIMIT);

export const DepartmentHealthSkillCoverageMap = ({skillCoverage, loading = false, embedded = false}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.skillCoverageMap'});
  const currentLanguage = useContext(LanguageContext);

  const columns = useMemo(() => {
    if (!skillCoverage) {
      return null;
    }

    return coverageColumns.map((column) => ({
      ...column,
      items: limitItems(skillCoverage[column.key]),
    }));
  }, [skillCoverage]);

  if (loading || !columns) {
    return <DepartmentHealthSkillCoverageMapSkeleton embedded={embedded} />;
  }

  const cardSx = embedded ? {...departmentHealthSectionCardSx, mb: 0} : departmentHealthSectionCardSx;

  return (
    <EmpionPaper sx={cardSx} data-testid='department-health-skill-coverage-map'>
      <Box component='h2' sx={departmentHealthSectionTitleSx}>
        <Box component='span' sx={{color: 'text.secondary', display: 'flex', alignItems: 'center'}}>
          <BarChartOutlinedIcon sx={{fontSize: 15}} />
        </Box>
        {t('title')}
      </Box>

      <Typography variant='body2' sx={{...departmentHealthSectionSubtitleSx, mb: 1.75}}>
        {t('subtitle')}
      </Typography>

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'}, gap: 2.5}}>
        {columns.map((column) => (
          <Box key={column.key}>
            <Typography
              sx={{
                fontSize: '10.5px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: column.textColor,
                mb: 1.125,
                display: 'flex',
                alignItems: 'center',
                gap: 0.625,
              }}
            >
              <Box
                component='span'
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: column.color,
                  flexShrink: 0,
                }}
              />
              {t(column.labelKey, {count: column.items.length})}
            </Typography>

            {column.items.length === 0 ? (
              <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>{t('emptyColumn')}</Typography>
            ) : (
              column.items.map((item) => {
                const skillLabel = getLocalisedPropertyValue(item.skillName, currentLanguage);

                return (
                  <Box
                    key={`${column.key}-${skillLabel}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.75,
                      borderBottom: '0.5px solid',
                      borderColor: 'divider',
                      '&:last-of-type': {borderBottom: 'none'},
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: '12px',
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {skillLabel}
                    </Typography>

                    {item.coveragePct < 100 && (
                      <Box
                        sx={{
                          width: 48,
                          height: 4,
                          bgcolor: 'action.hover',
                          borderRadius: '99px',
                          overflow: 'hidden',
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: `${item.coveragePct}%`,
                            height: '100%',
                            bgcolor: column.color,
                            borderRadius: '99px',
                          }}
                        />
                      </Box>
                    )}

                    <Typography
                      sx={{
                        fontSize: '11.5px',
                        fontWeight: 600,
                        color: column.textColor,
                        minWidth: 32,
                        textAlign: 'right',
                        flexShrink: 0,
                      }}
                    >
                      {item.coveragePct}%
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        ))}
      </Box>
    </EmpionPaper>
  );
};
